// src/features/mech/sheetmetal/questions/SheetmetalQuestions.jsx
import React, { useState } from "react";
import QuestionsSection from "../../../../shared/sections/QuestionsSection";
import PartNameQuestion from "./PartNameQuestion";
import SelectProcessQuestion from "./SelectProcessQuestion";
import MaterialSelectionQuestion from "./MaterialSelectionQuestion";
import MaterialGradeQuestion from "./MaterialGradeQuestion";
import PartWeightDimensionsQuestion from "./PartWeightDimensionsQuestion";
import OperationsQuestion from "./operations";
import SurfaceFinish from "./SurfaceFinish";
import AnnualVolumeQuestion from "./AnnualVolumeQuestion";
import ManufacturingCountrySelect from "./ManufacturingCountrySelect";

/**
 * CookiesQuestionsSection Component
 *
 * Encapsulates all product-related questions for Cookies commodity
 * Manages form data, validation, and question flow
 *
 * @param {function} onComplete - Callback when all questions are answered
 * @param {function} onDataChange - Callback when form data changes
 * @param {object} initialData - Initial data to populate the form
 * @param {function} onReset - Optional callback when reset button is clicked
 */
export default function SheetmetalQuestions({ onComplete, onDataChange, initialData, onReset }) {
  const materialValidationRef = React.useRef(null);
  const materialGradeValidationRef = React.useRef(null);
  const processSelectionRef = React.useRef(null);
  const [materialValidation, setMaterialValidation] = React.useState({
    isValid: false,
    isMaterialValid: false,
    isValidating: false,
  });
  const [materialGradeValidation, setMaterialGradeValidation] = React.useState({
    isValid: true,
    isGradeValid: true,
    isValidating: false,
  });
  const [demoMode, setDemoMode] = useState(false);

  const [productFormData, setProductFormData] = useState(
    initialData || {
      productName: "",
      materialSelection: "",
      materialGrade: "",
      processSelection: "",
      partWeightDimensions: {
        finishWeight: "",
        partLength: "",
        partWidth: "",
        partHeight: "",
      },
      operations: {
        operationsCount: "",
        timePerBend: "",
        deburringEnabled: "",
        deburringCycleTime: "",
        noOfBendsPressBrake: "",
        averageBendingLength: "",
        noOfBends: "",
        noOfTapsHoles: "",
        weldingLength: "",
        imageName: "",
        imageData: "",
      },
      surfaceFinish: {
        paintingMaterial: "",
        paintingSurfaceArea: "",
        paintingThickness: "",
        platingMaterial: "",
        platingSurfaceArea: "",
        platingThickness: "",
      },
      annualVolume: "",
      manufacturingCountry: "",
    }
  );   // Calculate initial step based on answered questions
  const getInitialStep = () => {
    if (!initialData) return 0;

    if (initialData.manufacturingCountry) return 7; // Last question
    if (initialData.annualVolume) return 6;
    const hasDimensions =
      initialData.partWeightDimensions &&
      (initialData.partWeightDimensions.finishWeight ||
        initialData.partWeightDimensions.partLength ||
        initialData.partWeightDimensions.partWidth ||
        initialData.partWeightDimensions.partHeight);
    if (hasDimensions) return 5;
    const hasOperations =
      initialData.operations &&
      (initialData.operations.operationsCount ||
        initialData.operations.imageName);
    if (hasOperations) return 4;
    if (initialData.processSelection) return 3;
    if (initialData.materialGrade) return 2; // Material grade step
    if (initialData.materialSelection) return 1; // Material selection step
    if (initialData.productName) return 0;
    return 0;
  };

  const [productChatStep, setProductChatStep] = useState(getInitialStep());

  // Check if all questions are answered
  const materialValue = typeof productFormData.materialSelection === "string"
    ? productFormData.materialSelection
    : (typeof productFormData.materialSelection === "object"
      ? productFormData.materialSelection?.material || ""
        : productFormData.materialSelection || "");

  const isAllAnswered =
    productFormData.productName?.trim() &&
    materialValue.trim() &&
    productFormData.processSelection?.trim() &&
    productFormData.partWeightDimensions?.finishWeight?.toString().trim() &&
    productFormData.partWeightDimensions?.partLength?.toString().trim() &&
    productFormData.partWeightDimensions?.partWidth?.toString().trim() &&
    productFormData.partWeightDimensions?.partHeight?.toString().trim() &&
    productFormData.operations?.operationsCount?.toString().trim() &&
    productFormData.annualVolume &&
    productFormData.manufacturingCountry;

  // Notify parent when data changes and all questions are answered
  React.useEffect(() => {
    if (isAllAnswered) {
      onDataChange?.(productFormData);
    }
    // Don't call onDataChange(null) when incomplete - this would wipe parent state
    // Parent should retain the last valid data for persistence across navigation
  }, [productFormData, isAllAnswered, onDataChange]);

  // Selected values for display
  const materialDisplay = materialValue
    ? productFormData.materialGrade
      ? `${materialValue} (Grade: ${productFormData.materialGrade})`
      : materialValue
    : "";

  const buildOperationsDisplay = () => {
    const ops = productFormData.operations || {};
    const parts = [];

    // Normalize process name for comparison (case-insensitive)
    const normalizedProcess = (productFormData.processSelection || "").toLowerCase().trim();
    const isTurretOrLaser = normalizedProcess === "turret" || normalizedProcess === "laser" || normalizedProcess.includes("laser cutting");
    const isStageOrProgressive = normalizedProcess === "stage" || normalizedProcess === "progressive";

    // Cavities defaults to 1 in backend, not shown in display
    
    // Hide "Operations" if Process is Turret or Laser cutting
    if (!isTurretOrLaser && ops.operationsCount) {
      parts.push(`Operations: ${ops.operationsCount}`);
    }
    
    if (ops.deburringEnabled) parts.push(`Deburring: ${ops.deburringEnabled}`);
    if (ops.deburringEnabled === "Yes" && ops.deburringCycleTime) {
      parts.push(`Deburring cycle time: ${ops.deburringCycleTime} sec`);
    }
    
    // Show "No of Bends" only if Process is Turret or Laser
    if (isTurretOrLaser && ops.noOfBends) {
      parts.push(`No of Bends: ${ops.noOfBends}`);
    }
    
    // Hide "Bends" (NoOfBends_PressBrake) if Process is Stage or Progressive
    if (!isStageOrProgressive && ops.noOfBendsPressBrake) {
      parts.push(`Bends: ${ops.noOfBendsPressBrake}`);
    }
    
    // Hide "Average Bending Length" if Process is Stage or Progressive
    if (!isStageOrProgressive && ops.averageBendingLength) {
      parts.push(`Average Bending Length: ${ops.averageBendingLength}`);
    }
    
    // Hide "Time per bend" if Process is Stage or Progressive
    if (!isStageOrProgressive && ops.timePerBend) {
      parts.push(`Time per bend: ${ops.timePerBend} sec`);
    }
    
    if (ops.noOfTapsHoles) parts.push(`Taps/Holes: ${ops.noOfTapsHoles}`);
    if (ops.weldingLength) parts.push(`Welding length: ${ops.weldingLength} mm`);

    return parts.length > 0 ? parts.join(", ") : "";
  };

  const operationsDisplay = buildOperationsDisplay();

  const dimensionDisplay =
    productFormData.partWeightDimensions?.finishWeight ||
    productFormData.partWeightDimensions?.partLength ||
    productFormData.partWeightDimensions?.partWidth ||
    productFormData.partWeightDimensions?.partHeight
      ? `Weight: ${productFormData.partWeightDimensions?.finishWeight || "-"} g, ` +
        `L: ${productFormData.partWeightDimensions?.partLength || "-"} mm, ` +
        `W: ${productFormData.partWeightDimensions?.partWidth || "-"} mm, ` +
        `Thickness: ${productFormData.partWeightDimensions?.partHeight || "-"} mm`
      : "";

  const buildSurfaceFinishDisplay = () => {
    const sf = productFormData.surfaceFinish || {};
    const parts = [];

    // Painting
    if (sf.paintingMaterial || sf.paintingSurfaceArea || sf.paintingThickness) {
      const paintingParts = [];
      if (sf.paintingMaterial) paintingParts.push(`Material: ${sf.paintingMaterial}`);
      if (sf.paintingSurfaceArea) paintingParts.push(`Surface area: ${sf.paintingSurfaceArea} mm²`);
      if (sf.paintingThickness) paintingParts.push(`Thickness: ${sf.paintingThickness} microns`);
      if (paintingParts.length > 0) {
        parts.push(`Painting: ${paintingParts.join(", ")}`);
      }
    }

    // Plating
    if (sf.platingMaterial || sf.platingSurfaceArea || sf.platingThickness) {
      const platingParts = [];
      if (sf.platingMaterial) platingParts.push(`Material: ${sf.platingMaterial}`);
      if (sf.platingSurfaceArea) platingParts.push(`Surface area: ${sf.platingSurfaceArea} mm²`);
      if (sf.platingThickness) platingParts.push(`Thickness: ${sf.platingThickness} microns`);
      if (platingParts.length > 0) {
        parts.push(`Plating: ${platingParts.join(", ")}`);
      }
    }

    return parts.length > 0 ? parts.join("; ") : "";
  };

  const surfaceFinishDisplay = buildSurfaceFinishDisplay();

  const productSelectedValues = [
    { label: "Part Name:", value: productFormData.productName },
    { label: "Material:", value: materialDisplay },
    { label: "Process:", value: productFormData.processSelection },
    { label: "Operations:", value: operationsDisplay },
    { label: "Part Dimensions:", value: dimensionDisplay },
    { label: "Surface Finish:", value: surfaceFinishDisplay },
    { label: "Annual Volume:", value: productFormData.annualVolume },
    { label: "Manufacturing Country:", value: productFormData.manufacturingCountry },
  ];

  // Validation function to check if next button should be disabled
  const isNextDisabled = (step) => {
    switch (step) {
      case 0: // Product Name
        return !productFormData.productName || productFormData.productName.trim() === "";
      case 1: // Material Selection
        const matValue =
          typeof productFormData.materialSelection === "string"
            ? productFormData.materialSelection
            : (typeof productFormData.materialSelection === "object"
            ? productFormData.materialSelection?.material || ""
                : productFormData.materialSelection || "");
        // Check if material is entered
        if (!matValue || matValue.trim() === "") return true;
        // Check if validation is in progress - disable button while validating
        if (materialValidation.isValidating) return true;
        // Allow button to be enabled if material is entered (validation will happen on click)
        return false;
      case 2: // Material Grade Selection
        // Grade is optional, so always allow proceeding
        // Don't block navigation - grade validation happens on next button click if grade is provided
        return false;
      case 3: // Process Selection
        // Keep Next button enabled at all times - validation happens on click
        return false;
      case 4: // Operations
        return (
          !productFormData.operations?.operationsCount?.toString().trim()
        );
      case 5: // Part Weight & Dimensions
        {
          const d = productFormData.partWeightDimensions || {};
          const toNum = (v) => {
            if (v === null || v === undefined || v === "") return NaN;
            const n = typeof v === "number" ? v : parseFloat(v);
            return Number.isFinite(n) ? n : NaN;
          };
          const w = toNum(d.finishWeight);
          const l = toNum(d.partLength);
          const wdt = toNum(d.partWidth);
          const h = toNum(d.partHeight);
          return (
            !Number.isFinite(w) || w <= 0 ||
            !Number.isFinite(l) || l <= 0 ||
            !Number.isFinite(wdt) || wdt <= 0 ||
            !Number.isFinite(h) || h <= 0
          );
        }
      case 6: // Surface Finish (optional - no validation required)
        return false;
      case 7: // Annual Volume
        return !productFormData.annualVolume;
      case 8: // Manufacturing Country
        return !productFormData.manufacturingCountry;
      default:
        return false;
    }
  };

  const handleComplete = () => {
    onComplete?.(productFormData);
  };

  // Custom step change handler that validates before allowing step change
  const handleStepChange = async (newStep) => {
    // In demo mode, allow free navigation between steps (demo data is prefilled).
    // This prevents stale validation state from blocking forward navigation
    // after the user goes back to edit values like Part Name.
    if (demoMode) {
      setProductChatStep(newStep);
      return;
    }

    // If trying to move forward from material selection step (step 1) to step 2
    if (productChatStep === 1 && newStep === 2) {
      if (materialValidationRef.current) {
        // Validate material before allowing navigation
        const isValid = await materialValidationRef.current.validate();
        if (!isValid) {
          // Stay on the same step if validation fails
          return;
        }
        // Validation state should be updated via onValidationChange callback
        // Wait for state to update before navigating
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      // Material is valid, proceed to step 2 (Material Grade)
      // Explicitly set to step 2 and return to prevent any other logic from running
      setProductChatStep(2);
      return;
    }
    
    // If trying to move forward from material grade step (step 2)
    if (productChatStep === 2 && newStep === 3) {
      // If grade is provided, validate it before allowing navigation
      if (productFormData.materialGrade?.trim() && materialGradeValidationRef.current) {
        const isValid = await materialGradeValidationRef.current.validate();
        if (!isValid) {
          // Stay on the same step if validation fails
          return;
        }
      }
      // Grade is optional, so allow proceeding even if empty
      setProductChatStep(3);
      return;
    }
    
    // If trying to move forward from process selection step (step 3) to step 4 (Operations)
    if (productChatStep === 3 && newStep === 4) {
      // Validate process before allowing navigation
      if (processSelectionRef.current && processSelectionRef.current.validate) {
        const isValid = await processSelectionRef.current.validate();
        if (!isValid) {
          // Stay on the same step if validation fails
          return;
        }
      }
      // Process is valid, proceed to step 4 (Operations)
      setProductChatStep(4);
      return;
    }
    
    // Allow going to step 2 only after validation from step 1
    // If trying to go to step 2 from any other step, check if material is valid
    if (newStep > productChatStep && newStep === 2 && productChatStep !== 1) {
      // Only allow going to step 2 if material is valid (coming from other steps)
      if (!materialValidation.isMaterialValid) {
        return; // Prevent navigation to step 2 if material isn't valid
      }
    }
    
    // For any step change, check if next should be disabled
    if (newStep > productChatStep && isNextDisabled(productChatStep)) {
      // Don't allow navigation if current step is invalid
      return;
    }
    
    // Allow step change
    setProductChatStep(newStep);
  };

  // Helper function to handle automatic navigation to next step on Enter key
  const handleNextStep = async (stepIndex) => {
    // Use handleStepChange to ensure consistent validation logic
    const nextStep = stepIndex + 1;
    const totalSteps = 9; // Total number of questions (includes Material Grade and Surface Finish)
    
    if (nextStep < totalSteps) {
      // Use handleStepChange which handles validation
      await handleStepChange(nextStep);
    } else {
      handleComplete();
    }
  };

  // Reset function to clear all fields and start from first question
  const handleReset = () => {
    setProductFormData({
      productName: "",
      materialSelection: "",
      materialGrade: "",
      processSelection: "",
      partWeightDimensions: {
        finishWeight: "",
        partLength: "",
        partWidth: "",
        partHeight: "",
      },
      operations: {
        operationsCount: "",
        timePerBend: "",
        deburringEnabled: "",
        deburringCycleTime: "",
        noOfBendsPressBrake: "",
        averageBendingLength: "",
        noOfBends: "",
        noOfTapsHoles: "",
        weldingLength: "",
        imageName: "",
        imageData: "",
      },
      surfaceFinish: {
        paintingMaterial: "",
        paintingSurfaceArea: "",
        paintingThickness: "",
        platingMaterial: "",
        platingSurfaceArea: "",
        platingThickness: "",
      },
      annualVolume: "",
      manufacturingCountry: "",
    });
    setProductChatStep(0);
    // Call parent reset handler if provided
    if (onReset) {
      onReset();
    }
  };

  // Handle demo toggle
  const handleDemoToggle = (checked) => {
    setDemoMode(checked);
    if (checked) {
      // Mark validations as valid for prefilled demo data.
      setMaterialValidation({
        isValid: true,
        isMaterialValid: true,
        isValidating: false,
      });
      setMaterialGradeValidation({
        isValid: true,
        isGradeValid: true,
        isValidating: false,
      });

      // Set demo values when toggle is turned on
      setProductFormData({
        productName: "lugs",
        materialSelection: "CRS",
        materialGrade: "",
        processSelection: "Turret Punch",
        partWeightDimensions: {
          finishWeight: "200",
          partLength: "85",
          partWidth: "85",
          partHeight: "2",
        },
        operations: {
          operationsCount: "2",
          timePerBend: "6",
          deburringEnabled: "Yes",
          deburringCycleTime: "",
          noOfBendsPressBrake: "",
          averageBendingLength: "200",
          noOfBends: "1",
          noOfTapsHoles: "1",
          weldingLength: "1",
          imageName: "",
          imageData: "",
        },
        surfaceFinish: {
          paintingMaterial: "Powder",
          paintingSurfaceArea: "1200",
          paintingThickness: "1",
          platingMaterial: "Copper",
          platingSurfaceArea: "1200",
          platingThickness: "1",
        },
        annualVolume: "Medium Volume",
        manufacturingCountry: "China",
      });
      // Navigate to the last step to show all filled values
      setProductChatStep(8);
    } else {
      // Reset form when toggle is turned off
      handleReset();
    }
  };

  return (
    <QuestionsSection
      selectedValues={productSelectedValues}
      currentStep={productChatStep}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      brandColor="#ff6034"
      isNextDisabled={isNextDisabled}
      onReset={handleReset}
      demoMode={demoMode}
      onDemoToggle={handleDemoToggle}
    >
      <PartNameQuestion
        value={productFormData.productName}
        onChange={(value) => setProductFormData({ ...productFormData, productName: value })}
        onNextStep={() => handleNextStep(0)}
        currentStep={productChatStep}
      />
      <MaterialSelectionQuestion
        ref={materialValidationRef}
        value={materialValue}
        onChange={(value) => {
            setProductFormData({
              ...productFormData,
              materialSelection: value,
            });
        }}
        onValidationChange={setMaterialValidation}
        onNextStep={() => handleNextStep(1)}
        currentStep={productChatStep}
      />
      <MaterialGradeQuestion
        ref={materialGradeValidationRef}
        value={productFormData.materialGrade || ""}
        materialType={materialValue}
        onChange={(value) => {
          setProductFormData({
            ...productFormData,
            materialGrade: value,
          });
        }}
        onValidationChange={setMaterialGradeValidation}
        onNextStep={() => handleNextStep(2)}
        currentStep={productChatStep}
        isMaterialValid={materialValidation.isMaterialValid}
        isValidatingMaterial={materialValidation.isValidating}
      />
      <SelectProcessQuestion
        ref={processSelectionRef}
        value={productFormData.processSelection}
        onChange={(value) => setProductFormData({ ...productFormData, processSelection: value })}
        onNextStep={() => handleNextStep(3)}
        currentStep={productChatStep}
      />
      <OperationsQuestion
        value={productFormData.operations}
        processSelection={productFormData.processSelection}
        onChange={(value) =>
          setProductFormData({
            ...productFormData,
            operations: {
              operationsCount: value?.operationsCount || "",
              timePerBend: value?.timePerBend || "",
              deburringEnabled: value?.deburringEnabled || "",
              deburringCycleTime: value?.deburringCycleTime || "",
              noOfBendsPressBrake: value?.noOfBendsPressBrake || "",
              averageBendingLength: value?.averageBendingLength || "",
              noOfBends: value?.noOfBends || "",
              noOfTapsHoles: value?.noOfTapsHoles || "",
              weldingLength: value?.weldingLength || "",
              imageName: value?.imageName || "",
              imageData: value?.imageData || "",
            },
          })
        }
        onNextStep={() => handleNextStep(4)}
        currentStep={productChatStep}
      />
      <PartWeightDimensionsQuestion
        value={productFormData.partWeightDimensions}
        onChange={(value) =>
          setProductFormData({
            ...productFormData,
            partWeightDimensions: value,
          })
        }
        onNextStep={() => handleNextStep(5)}
        currentStep={productChatStep}
      />
      <SurfaceFinish
        value={productFormData.surfaceFinish}
        onChange={(value) =>
          setProductFormData({
            ...productFormData,
            surfaceFinish: value,
          })
        }
        onNextStep={() => handleNextStep(6)}
      />
      <AnnualVolumeQuestion
        value={productFormData.annualVolume}
        onChange={(value) => setProductFormData({ ...productFormData, annualVolume: value })}
        onNextStep={() => handleNextStep(7)}
        currentStep={productChatStep}
      />
      <ManufacturingCountrySelect
        value={productFormData.manufacturingCountry}
        onChange={(value) =>
          setProductFormData({ ...productFormData, manufacturingCountry: value })
        }
      />
    </QuestionsSection>
  );
}
